import 'core-js/modules/es.array.map.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import React from 'react';
import { AvatarGroup as AvatarGroup$1 } from '@mui/joy';
import classNames from 'classnames';
import { Button } from '../button/button.js';
import { useConfigContextLoose } from '../config-provider/config-provider.js';
import { Dropdown } from '../dropdown/dropdown.js';
import { Menu } from '../menu/menu.js';

/**
 * AvatarGroup
 * =============================================================================
 * Formats a group of avatars. If the group contains more than 6 avatars, the
 * remaining ones will be shown in a dropdown.
 *
 * [Open docs »](https://cognitedata.github.io/cogs.js/?path=/docs/components-avatargroup--base)
 *
 * -----------------------------------------------------------------------------
 * Examples
 * -----------------------------------------------------------------------------
 * ```
 * <AvatarGroup>
 *   <Avatar text="Example Person"/>
 *   <Avatar text="Example Person"/>
 *   <Avatar text="Example Person"/>
 * </AvatarGroup>
 * ```
 *
 * -----------------------------------------------------------------------------
 * Properties
 * -----------------------------------------------------------------------------
 * @param size        - Change the size of the avatars
 * @param overflow    - Define after how many avatars the group should overflow
 * @param prominence  - Define if the AvatarGroup should be prominent
 * @param tooltip     - Define if the AvatarGroup should show Tooltips
 *
 * [Read the full documentation »](https://cognitedata.github.io/cogs.js/?path=/docs/components-avatargroup--base)
 */
var AvatarGroup = function AvatarGroup(_ref) {
  var children = _ref.children,
    _ref$size = _ref.size,
    size = _ref$size === undefined ? 'medium' : _ref$size,
    _ref$overflow = _ref.overflow,
    overflow = _ref$overflow === undefined ? 6 : _ref$overflow,
    tooltip = _ref.tooltip,
    className = _ref.className,
    appendTo = _ref.appendTo;
  var _a;
  var _useConfigContextLoos = useConfigContextLoose(),
    getOverlayContainer = _useConfigContextLoos.getOverlayContainer;
  var cloneAvatar = function cloneAvatar(child, newProps) {
    return /*#__PURE__*/React.cloneElement(child, newProps);
  };
  var childCount = React.Children.count(children);
  var appendToTarget = (_a = appendTo !== null && appendTo !== undefined ? appendTo : getOverlayContainer === null || getOverlayContainer === undefined ? undefined : getOverlayContainer()) !== null && _a !== undefined ? _a : 'parent';
  var isOverflowing = childCount > overflow;
  return jsxs(AvatarGroup$1, {
    role: "group",
    className: classNames('cogs-v10', 'cogs-avatar-group', "cogs-avatar-group--size-" + size, className),
    children: [React.Children.map(children, function (child, i) {
      if (isOverflowing && i < overflow - 1 || !isOverflowing && i < overflow) {
        return cloneAvatar(child, {
          stacked: i + 1 < childCount,
          size: size,
          tooltip: tooltip
        });
      }
      return null;
    }), childCount > overflow && jsx(Dropdown, {
      appendTo: appendToTarget,
      content: jsx(Menu, {
        children: React.Children.map(children, function (child, i) {
          if (i > overflow - 2) {
            return jsx(Menu.Item, {
              onClick: child.props.onClick,
              children: child.props.text
            });
          }
          return null;
        })
      }),
      children: jsxs(Button, {
        type: "secondary",
        className: "cogs-v10 cogs-avatar-group__overflow-button",
        children: ["+", childCount - overflow + 1]
      })
    })]
  });
};

export { AvatarGroup };
